// Единый стек «перекрытий» (модалки, правые дровера, поповеры) — чтобы Escape
// закрывал ТОЛЬКО верхнее, а не всё сразу.
//
// Правка владельца 2026-07-29: «зашёл в сделку → вкладка Абонементы → открыл
// абонемент → Escape — и закрылось всё; должен закрываться только абонемент».
// Причина: каждое окно вешало свой слушатель на window, и одно нажатие
// срабатывало у всех сразу. Стек был, но жил внутри Modal, и RightDrawer в нём
// не участвовал — поэтому дровер закрывался «заодно».
//
// КАК ОПРЕДЕЛЯЕМ ВЕРХНЕЕ. Порядок регистрации не годится: React выполняет
// эффекты ДЕТЕЙ раньше родителя, поэтому вложенная модалка вставала в стек
// перед дровером, внутри которого живёт, — и «верхним» оказывался дровер.
// Берём позицию в документе: узел, который идёт последним (в т.ч. вложенный
// внутрь другого), и есть верхний. Это же верно для модалки-соседа, отрисованной
// после дровера. Кто не дал узел — падает в конец по порядку регистрации.
//
// Слушатель на window ОДИН на всё приложение.
import { useEffect, useRef, type RefObject } from 'react';

interface Entry {
  close: () => void;
  /** Корневой узел перекрытия — по нему считаем, кто выше. */
  element: RefObject<HTMLElement | null> | undefined;
}

const stack: Entry[] = [];
let listenerBound = false;

/** true, если `b` перекрывает `a`: идёт в документе позже либо вложен в него. */
function isAbove(a: Entry, b: Entry): boolean {
  const ea = a.element?.current;
  const eb = b.element?.current;
  if (ea && eb) {
    return (ea.compareDocumentPosition(eb) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
  }
  // Узел дал только один — он и считается настоящим перекрытием.
  if (eb) return true;
  if (ea) return false;
  // Узла нет ни у кого — побеждает зарегистрированный позже (стек идёт по порядку).
  return true;
}

function topEntry(): Entry | undefined {
  let top: Entry | undefined;
  for (const entry of stack) {
    if (!top || isAbove(top, entry)) top = entry;
  }
  return top;
}

function onWindowKeyDown(e: KeyboardEvent): void {
  if (e.key !== 'Escape') return;
  const top = topEntry();
  if (!top) return;
  // Гасим событие: ниже по стеку никто не должен считать этот Escape своим.
  e.stopPropagation();
  top.close();
}

/**
 * Закрывать этот оверлей по Escape, но только если он верхний.
 *
 * @param onClose    закрытие оверлея
 * @param active     участвует ли оверлей в стеке сейчас (обычно `open`)
 * @param elementRef корневой узел оверлея — по нему определяется, кто выше
 */
export function useEscapeToClose(
  onClose: () => void,
  active = true,
  elementRef?: RefObject<HTMLElement | null>,
): void {
  // Держим свежий onClose в ref: иначе смена коллбэка на каждом рендере
  // перерегистрировала бы запись в стеке.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active) return undefined;

    const entry: Entry = { close: () => onCloseRef.current(), element: elementRef };
    stack.push(entry);
    if (!listenerBound) {
      window.addEventListener('keydown', onWindowKeyDown);
      listenerBound = true;
    }

    return () => {
      const i = stack.lastIndexOf(entry);
      if (i !== -1) stack.splice(i, 1);
    };
    // elementRef — стабильный объект из useRef: включаем в зависимости, но
    // перерегистрации это не вызывает.
  }, [active, elementRef]);
}

/** Только для тестов: очистить стек между кейсами. */
export function resetOverlayStackForTests(): void {
  stack.length = 0;
}
